/* eslint-disable */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Users, Network, BarChart3, Receipt, Image as ImageIcon,
  ShieldCheck, Upload, Target,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Panel, EmptyState, Loading, SectionLabel,
} from "@/components/app/primitives";
import { EditMetricDialog, TargetDialog, CacExpensesPanel } from "@/components/app/MetricsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  moneyCompact, moneyFull, pct, months as monthsFmt, ratioLabel, monthLabel, EMPTY_MARK,
} from "@/lib/format";

const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "founder", label: "Founder" },
  { value: "operating_partner", label: "Operating partner" },
  { value: "advisor", label: "Advisor" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
const NONE = "__none__";

// ----------------------------------------------------------------------------
// Teams tab
// ----------------------------------------------------------------------------
function TeamDialog({ onOpenChange, team, people, onSaved }) {
  const blank = { name: "", region: "", description: "", lead_person_id: NONE, order: 0 };
  const [form, setForm] = useState(() => (team ? { ...blank, ...team, lead_person_id: team.lead_person_id || NONE } : blank));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) { toast.error("Team needs a name"); return; }
    setSaving(true);
    const payload = {
      name: form.name, region: form.region, description: form.description,
      lead_person_id: form.lead_person_id === NONE ? "" : form.lead_person_id,
      order: Number(form.order) || 0,
    };
    try {
      if (team) await api.patch(`/teams/${team.id}`, payload);
      else await api.post("/teams", payload);
      toast.success(team ? "Team updated" : "Team created");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save team"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="admin-team-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">{team ? "Edit team" : "New team"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Name</Label>
            <Input data-testid="admin-team-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Region</Label>
            <Input value={form.region} onChange={(e) => set("region", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Lead</Label>
            <Select value={form.lead_person_id} onValueChange={(v) => set("lead_person_id", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No lead</SelectItem>
                {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter><Button data-testid="admin-team-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save team"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamsTab({ teams, people, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const remove = async (t) => {
    try { await api.delete(`/teams/${t.id}`); toast.success("Team removed"); reload(); }
    catch (e) { toast.error("Could not remove team"); }
  };
  return (
    <Panel
      title="Teams"
      bodyClassName="p-4"
      action={<Button data-testid="admin-team-add" size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-3.5 w-3.5" strokeWidth={1.75} /> New team</Button>}
    >
      {teams.length === 0 ? <EmptyState message="No teams yet." /> : (
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <Table data-testid="admin-teams-table">
            <TableHeader><TableRow className="bg-background/40 hover:bg-background/40">
              <TableHead className="text-[11px] uppercase tracking-[0.12em]">Team</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.12em]">Region</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.12em]">Lead</TableHead>
              <TableHead className="text-center text-[11px] uppercase tracking-[0.12em]">Members</TableHead>
              <TableHead className="w-20" />
            </TableRow></TableHeader>
            <TableBody>
              {teams.map((t) => (
                <TableRow key={t.id} className="hover:bg-white/5">
                  <TableCell className="text-sm text-foreground">{t.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.region || EMPTY_MARK}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.lead_name || EMPTY_MARK}</TableCell>
                  <TableCell className="text-center font-mono tabular-nums text-xs">{t.member_count}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 justify-end">
                      <button data-testid={`admin-team-edit-${t.id}`} onClick={() => { setEditing(t); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" strokeWidth={1.75} /></button>
                      <button data-testid={`admin-team-delete-${t.id}`} onClick={() => remove(t)} className="text-muted-foreground hover:text-[#FFB4B4]"><Trash2 className="h-4 w-4" strokeWidth={1.75} /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {open && <TeamDialog onOpenChange={setOpen} team={editing} people={people} onSaved={reload} />}
    </Panel>
  );
}

// ----------------------------------------------------------------------------
// People and org tab
// ----------------------------------------------------------------------------
function PersonDialog({ onOpenChange, person, teams, people, onSaved }) {
  const [form, setForm] = useState(() => ({
    role_title: person.role_title || "", role: person.role || "operating_partner",
    team_id: person.team_id || NONE, manager_id: person.manager_id || NONE,
    email: person.email || "", phone: person.phone || "", location: person.location || "",
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    const payload = {
      role_title: form.role_title, role: form.role,
      team_id: form.team_id === NONE ? "" : form.team_id,
      manager_id: form.manager_id === NONE ? "" : form.manager_id,
      email: form.email, phone: form.phone, location: form.location,
    };
    try {
      await api.patch(`/people/${person.id}`, payload);
      toast.success("Team member updated");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setSaving(false); }
  };
  if (!person) return null;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="admin-person-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">{person.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2"><Label className="text-xs text-muted-foreground">Title</Label>
            <Input data-testid="admin-person-title" value={form.role_title || ""} onChange={(e) => set("role_title", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Directory role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Team</Label>
            <Select value={form.team_id} onValueChange={(v) => set("team_id", v)}>
              <SelectTrigger data-testid="admin-person-team"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No team</SelectItem>
                {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5 col-span-2"><Label className="text-xs text-muted-foreground">Reports to</Label>
            <Select value={form.manager_id} onValueChange={(v) => set("manager_id", v)}>
              <SelectTrigger data-testid="admin-person-manager"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No manager</SelectItem>
                {people.filter((p) => p.id !== person.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Email</Label>
            <Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Phone</Label>
            <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div className="space-y-1.5 col-span-2"><Label className="text-xs text-muted-foreground">Location</Label>
            <Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
        </div>
        <DialogFooter><Button data-testid="admin-person-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save member"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PeopleTab({ people, teams, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const teamName = (id) => teams.find((t) => t.id === id)?.name;
  const personName = (id) => people.find((p) => p.id === id)?.name;
  return (
    <Panel title="People and organisation" bodyClassName="p-4">
      <div className="rounded-md border border-border bg-card overflow-hidden">
        <Table data-testid="admin-people-table">
          <TableHeader><TableRow className="bg-background/40 hover:bg-background/40">
            <TableHead className="text-[11px] uppercase tracking-[0.12em]">Name</TableHead>
            <TableHead className="text-[11px] uppercase tracking-[0.12em]">Title</TableHead>
            <TableHead className="text-[11px] uppercase tracking-[0.12em]">Team</TableHead>
            <TableHead className="text-[11px] uppercase tracking-[0.12em]">Reports to</TableHead>
            <TableHead className="w-10" />
          </TableRow></TableHeader>
          <TableBody>
            {people.map((p) => (
              <TableRow key={p.id} className="hover:bg-white/5">
                <TableCell className="text-sm text-foreground">{p.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.role_title || EMPTY_MARK}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{teamName(p.team_id) || EMPTY_MARK}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{personName(p.manager_id) || EMPTY_MARK}</TableCell>
                <TableCell>
                  <button data-testid={`admin-person-edit-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" strokeWidth={1.75} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {open && editing && <PersonDialog onOpenChange={setOpen} person={editing} teams={teams} people={people} onSaved={reload} />}
    </Panel>
  );
}

// ----------------------------------------------------------------------------
// Venture workspace (shared by Metrics tab and CAC tab)
// ----------------------------------------------------------------------------
function VentureSelect({ ventures, value, onChange }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid="admin-venture-select" className="h-9 w-[260px]"><SelectValue placeholder="Select a venture" /></SelectTrigger>
      <SelectContent>{ventures.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function MetricsTab({ ventures }) {
  const [vid, setVid] = useState(ventures[0]?.id || "");
  const [cc, setCc] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!vid) return;
    setLoading(true);
    try { const res = await api.get(`/mission-control/ventures/${vid}`); setCc(res.data); }
    catch (e) { toast.error("Could not load venture metrics"); }
    finally { setLoading(false); }
  }, [vid]);
  useEffect(() => { load(); }, [load]);
  const months = cc && !cc.restricted ? cc.months || [] : [];
  return (
    <Panel
      title="Ventures and metrics"
      bodyClassName="p-4"
      action={(
        <div className="flex items-center gap-2">
          <VentureSelect ventures={ventures} value={vid} onChange={setVid} />
          {cc && !cc.restricted && (
            <>
              <TargetDialog ventureId={vid} target={cc.ltv_cac_target} onSaved={load}
                trigger={<Button size="sm" variant="outline" className="h-8 gap-1.5"><Target className="h-3.5 w-3.5" strokeWidth={1.75} /> Target {ratioLabel(cc.ltv_cac_target)}</Button>} />
              <EditMetricDialog ventureId={vid} monthsData={months} defaultMonth={months[months.length - 1]?.month} onSaved={load}
                trigger={<Button size="sm" className="h-8 gap-1.5 bg-primary hover:bg-[#2746c0]"><Plus className="h-3.5 w-3.5" strokeWidth={1.75} /> Add or edit month</Button>} />
            </>
          )}
        </div>
      )}
    >
      {loading ? <Loading label="Loading metrics" /> : !cc ? <EmptyState message="Select a venture." /> : cc.restricted ? <EmptyState message="Metrics restricted." /> : months.length === 0 ? <EmptyState message="No months recorded. Use Add or edit month." /> : (
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <Table data-testid="admin-metrics-table">
            <TableHeader><TableRow className="bg-background/40 hover:bg-background/40">
              <TableHead className="text-[11px] uppercase tracking-[0.12em]">Month</TableHead>
              <TableHead className="text-right text-[11px] uppercase tracking-[0.12em]">Cash</TableHead>
              <TableHead className="text-right text-[11px] uppercase tracking-[0.12em]">Burn</TableHead>
              <TableHead className="text-right text-[11px] uppercase tracking-[0.12em]">Revenue</TableHead>
              <TableHead className="text-right text-[11px] uppercase tracking-[0.12em]">Runway</TableHead>
              <TableHead className="text-right text-[11px] uppercase tracking-[0.12em]">Churn</TableHead>
              <TableHead className="text-right text-[11px] uppercase tracking-[0.12em]">LTV:CAC</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {months.map((m) => (
                <TableRow key={m.month} className="hover:bg-white/5">
                  <TableCell className="font-mono text-xs text-foreground">{monthLabel(m.month)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs">{moneyCompact(m.cash_in_bank)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs">{moneyCompact(m.monthly_burn)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs">{moneyCompact(m.revenue)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs">{monthsFmt(m.computed?.runway_months)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs">{pct(m.churn_rate)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs">{ratioLabel(m.computed?.ltv_cac_ratio)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Panel>
  );
}

function CacTab({ ventures }) {
  const [vid, setVid] = useState(ventures[0]?.id || "");
  const [cc, setCc] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!vid) return;
    setLoading(true);
    try { const res = await api.get(`/mission-control/ventures/${vid}`); setCc(res.data); }
    catch (e) { toast.error("Could not load CAC expenses"); }
    finally { setLoading(false); }
  }, [vid]);
  useEffect(() => { load(); }, [load]);
  return (
    <Panel title="CAC expenses" bodyClassName="p-4" action={<VentureSelect ventures={ventures} value={vid} onChange={setVid} />}>
      {loading ? <Loading label="Loading expenses" /> : !cc ? <EmptyState message="Select a venture." /> : cc.restricted ? <EmptyState message="Restricted." /> : (
        <CacExpensesPanel
          ventureId={vid}
          expenses={cc.cac_expenses}
          categories={cc.cac_categories}
          guidance={cc.cac_guidance}
          canEdit={cc.can_edit}
          defaultMonth={cc.latest?.month}
          onChanged={load}
        />
      )}
    </Panel>
  );
}

// ----------------------------------------------------------------------------
// Structure image tab
// ----------------------------------------------------------------------------
function StructureTab() {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/mission-control/overview");
      const aid = res.data.structure_image?.asset_id;
      if (aid) { const img = await api.get(`/mission-control/structure-image/${aid}`); setUrl(img.data.data_url); }
      else setUrl(null);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("scope", "studio");
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/mission-control/structure-image", fd);
      toast.success("Company structure image updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async () => {
    try { await api.delete("/mission-control/structure-image?scope=studio"); toast.success("Image removed"); setUrl(null); }
    catch (e) { toast.error("Could not remove image"); }
  };

  return (
    <Panel title="Company structure image" bodyClassName="p-4">
      <p className="mb-3 text-xs text-muted-foreground">This image appears on the Mission Control overview for everyone. Upload a PNG or JPG up to 4 MB.</p>
      <div className="flex items-center gap-3">
        <label data-testid="admin-structure-upload-label" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground hover:bg-white/5 transition-colors">
          <Upload className="h-4 w-4" strokeWidth={1.75} /> {uploading ? "Uploading" : "Upload image"}
          <input data-testid="admin-structure-upload-input" type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
        </label>
        {url && <Button data-testid="admin-structure-remove" variant="outline" size="sm" className="h-9" onClick={remove}>Remove</Button>}
      </div>
      <div className="mt-4">
        {loading ? <Loading label="Loading" /> : url ? (
          <img data-testid="admin-structure-preview" src={url} alt="Company structure" className="max-h-[420px] w-full rounded-md border border-border object-contain bg-background/40" />
        ) : (
          <EmptyState message="No structure image uploaded yet." />
        )}
      </div>
    </Panel>
  );
}

// ----------------------------------------------------------------------------
// Access and roles tab (Owner only)
// ----------------------------------------------------------------------------
function AccessTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/people/users"); setUsers(res.data.users || []); }
    catch (e) { toast.error("Could not load users"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const changeRole = async (u, role) => {
    try { await api.patch(`/people/users/${u.id}/role`, { role }); toast.success(`${u.email} set to ${ROLE_LABEL[role]}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change role"); }
  };
  return (
    <Panel title="Access and roles" bodyClassName="p-4">
      {loading ? <Loading label="Loading users" /> : users.length === 0 ? <EmptyState message="No users have signed in yet." /> : (
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <Table data-testid="admin-users-table">
            <TableHeader><TableRow className="bg-background/40 hover:bg-background/40">
              <TableHead className="text-[11px] uppercase tracking-[0.12em]">Name</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.12em]">Email</TableHead>
              <TableHead className="w-[200px] text-[11px] uppercase tracking-[0.12em]">Role</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="hover:bg-white/5">
                  <TableCell className="text-sm text-foreground">{u.name || EMPTY_MARK}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger data-testid={`admin-user-role-${u.id}`} className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Panel>
  );
}

// ----------------------------------------------------------------------------
export default function AdminConsole() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [teams, setTeams] = useState([]);
  const [people, setPeople] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTeams = useCallback(async () => {
    try { const res = await api.get("/teams"); setTeams(res.data.teams || []); } catch (e) { /* ignore */ }
  }, []);
  const loadPeople = useCallback(async () => {
    try { const res = await api.get("/people"); setPeople(res.data.people || []); } catch (e) { /* ignore */ }
  }, []);
  const loadVentures = useCallback(async () => {
    try { const res = await api.get("/ventures"); setVentures(Array.isArray(res.data) ? res.data : []); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([loadTeams(), loadPeople(), loadVentures()]).finally(() => setLoading(false));
  }, [loadTeams, loadPeople, loadVentures]);

  const reloadOrg = () => { loadTeams(); loadPeople(); };

  if (loading) return <Loading label="Loading Admin console" />;

  return (
    <div className="space-y-4">
      <Tabs defaultValue="teams" className="w-full">
        <TabsList data-testid="admin-tabs" className="flex flex-wrap h-auto gap-1 bg-card border border-border p-1">
          <TabsTrigger value="teams" data-testid="admin-tab-teams" className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-white"><Users className="h-3.5 w-3.5" strokeWidth={1.75} /> Teams</TabsTrigger>
          <TabsTrigger value="people" data-testid="admin-tab-people" className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-white"><Network className="h-3.5 w-3.5" strokeWidth={1.75} /> People and org</TabsTrigger>
          <TabsTrigger value="metrics" data-testid="admin-tab-metrics" className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-white"><BarChart3 className="h-3.5 w-3.5" strokeWidth={1.75} /> Ventures and metrics</TabsTrigger>
          <TabsTrigger value="cac" data-testid="admin-tab-cac" className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-white"><Receipt className="h-3.5 w-3.5" strokeWidth={1.75} /> CAC expenses</TabsTrigger>
          <TabsTrigger value="structure" data-testid="admin-tab-structure" className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-white"><ImageIcon className="h-3.5 w-3.5" strokeWidth={1.75} /> Company structure</TabsTrigger>
          {isOwner && <TabsTrigger value="access" data-testid="admin-tab-access" className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-white"><ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} /> Access and roles</TabsTrigger>}
        </TabsList>

        <TabsContent value="teams" className="mt-4"><TeamsTab teams={teams} people={people} reload={reloadOrg} /></TabsContent>
        <TabsContent value="people" className="mt-4"><PeopleTab people={people} teams={teams} reload={reloadOrg} /></TabsContent>
        <TabsContent value="metrics" className="mt-4"><MetricsTab ventures={ventures} /></TabsContent>
        <TabsContent value="cac" className="mt-4"><CacTab ventures={ventures} /></TabsContent>
        <TabsContent value="structure" className="mt-4"><StructureTab /></TabsContent>
        {isOwner && <TabsContent value="access" className="mt-4"><AccessTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
